import { useTitle } from "@/hooks/use-title.hook.ts";
import { DemoUnavailable } from "@/components/demo-unavailable/DemoUnavailable.tsx";
import { getMcpServerUrl } from "@/helpers/mcp.helper.ts";
import {
  useIntegrationAdd,
  useIntegrations,
} from "@/queries/integrations/use-integrations.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { AppDistribution } from "@/types/common.types.ts";
import { ApiIntegrationType } from "@/types/integrations/integration.types.ts";
import { LinkTo, ListItem, Page, Skeleton, Stack, Title } from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useEffectOnce } from "react-use";

export default function SettingsIntegrations() {
  const { t } = useTranslation();
  const { data: integrations, isLoading } = useIntegrations();
  const navigate = useNavigate();

  useTitle(t("integrations:settings_integrations_title"), t("common:settings"));

  const add = useIntegrationAdd();
  const mcpServerUrl = getMcpServerUrl();

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.integrations.viewed);
  });

  const integrationTypesToConnect = useMemo(() => {
    if (!integrations) return [];
    const result: ApiIntegrationType[] = [];

    if (
      !integrations.some(
        (x) => x.integration_type === ApiIntegrationType.google_calendar_service_account
      )
    ) {
      result.push(ApiIntegrationType.google_calendar_service_account);
    }

    if (!integrations.some((x) => x.integration_type === ApiIntegrationType.slack)) {
      result.push(ApiIntegrationType.slack);
    }

    if (import.meta.env.VITE_APP_DISTRIBUTION == AppDistribution.skailer) {
      if (!integrations.some((x) => x.integration_type === ApiIntegrationType.google_workspace)) {
        result.push(ApiIntegrationType.google_workspace);
      }
    }

    if (!integrations.some((x) => x.integration_type === ApiIntegrationType.zoom)) {
      result.push(ApiIntegrationType.zoom);
    }
    return result;
  }, [integrations]);

  const addIntegration = async (type: ApiIntegrationType) => {
    try {
      const data = await add.mutateAsync(type);
      navigate(`/settings/integrations/${data.id}/${type}`);
    } catch {
      return;
    }
  };

  const getIntegrationTypeCaption = (type: ApiIntegrationType) => {
    switch (type) {
      case ApiIntegrationType.google_calendar_service_account:
        return t("integrations:google_calendar_title");
      case ApiIntegrationType.slack:
        return t("integrations:slack_title");
      case ApiIntegrationType.google_workspace:
        return t("integrations:google_workspace_title");
      case ApiIntegrationType.zoom:
        return t("integrations:zoom_title");
      default:
        return "";
    }
  };

  return (
    <>
      <Page>
        <Page.Header
          title={t("integrations:settings_integrations_title")}
          showBack={true}
          navigateTo={"/settings"}
        />
        <Page.Content>
          <Stack gap={"xl"} className={"container-sm mx-auto"}>
            <Title
              size={"lg"}
              paddingBottom
              paddingTop
              header={t("integrations:settings_integrations_title")}
              caption={t("integrations:settings_integrations_description")}
            />
            {isLoading && <Skeleton className={"w-full h-24"} />}
            {integrations && integrations.length > 0 && (
              <Stack>
                <Title size={"xs"} header={t("integrations:connected_integrations")} />
                {integrations.map((integration) => (
                  <ListItem
                    key={integration.id}
                    title={getIntegrationTypeCaption(integration.integration_type)}
                    caption={
                      integration.is_active
                        ? t("integrations:is_active")
                        : `${t("integrations:not_active")}`
                    }
                    valueSlot={
                      <LinkTo
                        to={`/settings/integrations/${integration.id}/${integration.integration_type}`}
                      >
                        {t("integrations:manage")}
                      </LinkTo>
                    }
                  />
                ))}
              </Stack>
            )}
            {(integrationTypesToConnect.length > 0 || mcpServerUrl) && (
              <Stack>
                <Title size={"xs"} header={t("integrations:available_integrations")}></Title>
                {integrationTypesToConnect.includes(
                  ApiIntegrationType.google_calendar_service_account
                ) && (
                  <ListItem
                    title={t("integrations:google_calendar_title")}
                    caption={t("integrations:google_calendar_description")}
                    valueSlot={
                      <DemoUnavailable>
                        <LinkTo
                          onClick={() => {
                            addIntegration(ApiIntegrationType.google_calendar_service_account);
                          }}
                        >
                          {t("integrations:connect")}
                        </LinkTo>
                      </DemoUnavailable>
                    }
                  />
                )}
                {integrationTypesToConnect.includes(ApiIntegrationType.slack) && (
                  <ListItem
                    title={t("integrations:slack_title")}
                    caption={`${t("integrations:slack_description")}`}
                    valueSlot={
                      <DemoUnavailable>
                        <LinkTo
                          onClick={() => {
                            addIntegration(ApiIntegrationType.slack);
                          }}
                        >
                          {t("integrations:connect")}
                        </LinkTo>
                      </DemoUnavailable>
                    }
                  />
                )}
                {integrationTypesToConnect.includes(ApiIntegrationType.google_workspace) && (
                  <ListItem
                    title={t("integrations:google_workspace_title")}
                    caption={`${t("integrations:google_workspace_description")}`}
                    valueSlot={
                      <DemoUnavailable>
                        <LinkTo
                          onClick={() => {
                            addIntegration(ApiIntegrationType.google_workspace);
                          }}
                        >
                          {t("integrations:connect")}
                        </LinkTo>
                      </DemoUnavailable>
                    }
                  />
                )}
                {integrationTypesToConnect.includes(ApiIntegrationType.zoom) && (
                  <ListItem
                    title={t("integrations:zoom_title")}
                    caption={t("integrations:zoom_description")}
                    valueSlot={
                      <DemoUnavailable>
                        <LinkTo
                          onClick={() => {
                            addIntegration(ApiIntegrationType.zoom);
                          }}
                        >
                          {t("integrations:connect")}
                        </LinkTo>
                      </DemoUnavailable>
                    }
                  />
                )}
                {mcpServerUrl && (
                  <ListItem
                    title={t("integrations:mcp_title")}
                    caption={t("integrations:mcp_organization_description")}
                    valueSlot={
                      <LinkTo
                        className={"whitespace-nowrap"}
                        target={"_blank"}
                        to={"/help/article/mcp-connection"}
                      >
                        {t("integrations:mcp_how_to_connect")}
                      </LinkTo>
                    }
                  />
                )}
              </Stack>
            )}
          </Stack>
        </Page.Content>
      </Page>
    </>
  );
}
